'use client';

import { useCallback, useEffect, useRef } from 'react';
import type * as CasinoUserProfileWeb from '@fanduel/casino-user-profile-web';
import { useGlobalContext } from '@/store/contexts/global-context';
import { isXsell } from '@/logic/platform';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { getConfig } from '@/utils/config/config';
import { ddRumError } from '@/utils/datadog';
import { DD_USE_CASES } from '@/utils/datadog/types';
import type { ThemeVariant } from '@/theme/config/constants';

type CasinoUserProfileRuntimeConfig = {
    platform: string;
    product: string;
    hasFavouriteGamesIcon: boolean;
    themeVariant?: ThemeVariant;
};

let initialized = false;
let initializationPromise: Promise<void> | undefined;
let casinoUserProfileManager: typeof CasinoUserProfileWeb | null = null;

const resolveOrigins = (): { webOrigin: string; launcherOrigin: string } => {
    const config = getConfig();

    // When the game launcher runs same-origin, the profile UM must not build cross-origin
    // aggregation/launcher URLs — mirrors welcome-lobby's prior inline handling (CASTECH-617).
    // Empty origins signal the UM to resolve same-origin.
    if (config.sameOriginGameLauncher) {
        return { webOrigin: '', launcherOrigin: '' };
    }

    const aggregationHost = config.apis.AggregationDataClient.host ?? config.domain;
    const launcherHost = config.apis.LauncherClient.host;

    if (!aggregationHost) {
        ddRumError({
            name: 'CASINO_USER_PROFILE_MISSING_WEB_ORIGIN',
            description:
                'AggregationDataClient.host and domain are both empty; profile aggregation / user-activity calls may resolve to the wrong host.',
            use_case: DD_USE_CASES.GLOBAL
        });
    }
    if (!launcherHost) {
        ddRumError({
            name: 'CASINO_USER_PROFILE_MISSING_LAUNCHER_ORIGIN',
            description: 'LauncherClient.host is empty; profile game-launch links have no valid host.',
            use_case: DD_USE_CASES.GAME_LAUNCHER
        });
    }

    return {
        webOrigin: `${config.apis.AggregationDataClient.protocol ?? 'https'}://${aggregationHost}`,
        launcherOrigin: `${config.apis.LauncherClient.protocol ?? 'https'}://${launcherHost}`
    };
};

const runInitialize = async (runtimeConfig: CasinoUserProfileRuntimeConfig): Promise<void> => {
    const { initializeUnifiedModules } = await import('@/logic/unified-modules');
    await initializeUnifiedModules();

    const origins = resolveOrigins();
    const manager = await import('@fanduel/casino-user-profile-web');
    await manager.CasinoUserProfile.initialize({
        runtimeConfig: { ...runtimeConfig, ...origins }
    });
    casinoUserProfileManager = manager;
};

const initializeCasinoUserProfile = async (runtimeConfig: CasinoUserProfileRuntimeConfig): Promise<void> => {
    if (initialized) {
        return;
    }

    if (initializationPromise) {
        await initializationPromise;
        return;
    }

    initializationPromise = runInitialize(runtimeConfig).then(() => {
        initialized = true;
    });

    try {
        await initializationPromise;
    } catch (error) {
        initializationPromise = undefined;
        throw error;
    }
};

type UseCasinoUserProfileUmOptions = { enabled?: boolean; themeVariant?: ThemeVariant };

export const useCasinoUserProfileUm = ({ enabled = true, themeVariant }: UseCasinoUserProfileUmOptions = {}) => {
    const { isDesktop, featureFlags } = useGlobalContext();

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const runtimeConfig: CasinoUserProfileRuntimeConfig = {
            platform: isDesktop ? 'desktop' : 'mobile',
            product: isXsell() ? 'xsell' : 'casino',
            hasFavouriteGamesIcon: Boolean(featureFlags[PRODUCT_THROTTLES.FAVOURITE_GAMES]),
            ...(themeVariant !== undefined && { themeVariant })
        };

        void initializeCasinoUserProfile(runtimeConfig).catch((error) => {
            ddRumError({
                name: 'CASINO_USER_PROFILE_INIT_FAILED',
                description: 'Failed to initialize the casino user profile UM',
                use_case: DD_USE_CASES.GLOBAL,
                context: { message: error instanceof Error ? error.message : String(error) }
            });
        });
    }, [enabled, isDesktop, featureFlags, themeVariant]);

    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        if (themeVariant === undefined) return;
        const doUpdate = async () => {
            if (initializationPromise) {
                try {
                    await initializationPromise;
                } catch {
                    return;
                }
            }
            if (!initialized) return;
            const mod = await import('@fanduel/casino-user-profile-web');
            await mod.CasinoUserProfile.initialize({ runtimeConfig: { themeVariant } });
        };
        void doUpdate().catch(() => {});
    }, [enabled, themeVariant]);

    const displayUserProfile = useCallback(
        async (
            userId: string,
            displayName?: string,
            avatarUrl?: string,
            onGameInfoClick?: (gameUid: string) => void
        ): Promise<void> => {
            if (initializationPromise) {
                await initializationPromise;
            }
            await casinoUserProfileManager?.CasinoUserProfile.displayUserProfile(
                userId,
                displayName,
                avatarUrl,
                onGameInfoClick
            );
        },
        []
    );

    const displayProfileSetup = useCallback(async (onGameInfoClick?: (gameUid: string) => void): Promise<void> => {
        if (initializationPromise) {
            await initializationPromise;
        }
        await casinoUserProfileManager?.CasinoUserProfile.displayProfileSetup(onGameInfoClick);
    }, []);

    return { displayUserProfile, displayProfileSetup };
};
